import React from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import { StudioTheme, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../common/types';
import { opensDetails, StudioThemeState } from './studioRules';
import { studioThemeMessages } from './studioThemeMessages';
import { studioMessages } from './studioMessages';
import { fullVideoMessages } from './fullVideoMessages';
import { hasCardWork, StudioCardWork } from './studioCardWork';
import { ThemeCardWorkBar, ThemeCardWorkChips } from './ThemeCardWork';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STATE_CHIPS: Readonly<Record<Exclude<StudioThemeState, 'open'>, MessageDescriptor>> = {
  needsMaster: studioMessages.needsMaster,
  needsReleaseDate: studioMessages.needsReleaseDate,
  needsLyrics: fullVideoMessages.chipNeedsLyrics,
  needsPlan: fullVideoMessages.chipProPlan,
};

interface ThemeCardProps {
  theme: StudioTheme;
  state: StudioThemeState;
  /** The picture of the asset type in the song's theme (design q3a). */
  picture?: React.ReactNode;
  /** An open card shows "Ready to make" (design s7c: the full videos and the new short videos). */
  showReady?: boolean;
  /** The line under the card that says what to do (design s7c): add the lyrics, upgrade, wait. */
  note?: React.ReactNode;
  /**
   * The work of this asset type for the song (owner request 2026-10-06): "1 making", "2 ready",
   * "1 failed" in place of "Ready to make", and a bar while a run is being made.
   */
  work?: StudioCardWork;
  onPick: (themeId: StudioThemeId) => void;
}

/**
 * One asset type (designs l2–l4, n6, q3a, s7c): the picture in the song's theme, the credits, the
 * name, the line and a chip ("Ready to make", "Needs the master", "Needs a release date", "Needs
 * the lyrics", "Free plan", "Not ready yet"). A chip other than "Free plan" keeps the card shut; a
 * full video on Free still opens step 3, where "Make it" stays off. When the song has work of
 * this type, its chips ("1 making", "2 ready", "1 failed") take the place of "Ready to make".
 */
const ThemeCardComponent: React.FC<ThemeCardProps> = ({ theme, state, picture, showReady, note, work, onPick }) => {
  const intl = useIntl();
  const copy = studioThemeMessages[theme.id];
  const hasWork = hasCardWork(work);
  const isReady = state === 'open' && showReady === true && !hasWork;

  const card = (
    <button
      type="button"
      className={clsx(styles.card, note !== undefined && styles.cardJoined)}
      disabled={!opensDetails(state)}
      onClick={() => onPick(theme.id)}
      data-testid={`studio-theme-${theme.id}`}
      data-state={state}
      aria-busy={hasWork && work.making > 0 ? true : undefined}
    >
      {picture}
      <span className={styles.cardTop}>
        <span className={styles.credit} title={intl.formatMessage(studioMessages.credits, { count: theme.credits })}>
          {theme.credits}
        </span>
        {state !== 'open' && (
          <span
            className={clsx(styles.chip, state !== 'needsMaster' && state !== 'needsReleaseDate' && styles.chipOff)}
          >
            {intl.formatMessage(STATE_CHIPS[state])}
          </span>
        )}
        {isReady && (
          <span className={clsx(styles.chip, styles.chipOk)}>{intl.formatMessage(fullVideoMessages.chipReady)}</span>
        )}
        {hasWork && <ThemeCardWorkChips work={work} />}
      </span>
      <span className={styles.cardName}>{intl.formatMessage(copy.name)}</span>
      <span className={styles.cardLine}>{intl.formatMessage(copy.description)}</span>
      {hasWork && work.progress !== null && <ThemeCardWorkBar progress={work.progress} />}
    </button>
  );

  if (note === undefined) {
    return card;
  }
  return (
    <div className={styles.cardWithNote}>
      {card}
      <div className={styles.cardNote}>{note}</div>
    </div>
  );
};

export const ThemeCard = TypedMemo(ThemeCardComponent);
